<template>
  <div>
    <!-- Hero Section (2-column: text + 3D chat mockup) -->
    <section class="relative overflow-hidden min-h-screen flex items-center pt-16" data-animate="herotext" :class="{ 'is-visible': visible.herotext }">
      <div class="absolute inset-0 bg-white dark:bg-dark transition-colors duration-300">
        <img v-if="isDark" src="/ai_bg.webp" alt="" class="absolute inset-0 w-full h-full object-cover" />
        <img v-else src="/ai_bg_light.webp" alt="" class="absolute inset-0 w-full h-full object-cover opacity-40" />
      </div>

      <div class="container mx-auto px-4 relative z-10">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <p class="text-[13px] uppercase tracking-[0.2em] text-blue-500 font-medium mb-6 anim-el d1">
              {{ $t('chatbot.hero.tagline') }}
            </p>
            <h1 class="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1] mb-4 anim-el d2">
              <span class="text-gray-900 dark:text-white transition-colors duration-300">{{ $t('chatbot.hero.title') }}</span>
            </h1>
            <p class="text-lg md:text-xl text-gray-500 dark:text-gray-400 max-w-xl leading-relaxed mb-10 transition-colors duration-300 anim-el d3">
              {{ $t('chatbot.hero.description') }}
            </p>
            <div class="flex flex-col sm:flex-row gap-4 items-start mb-10 anim-el d4">
              <router-link to="/contact" class="group inline-flex items-center h-14 px-8 bg-gray-900 dark:bg-white text-white dark:text-dark rounded-xl font-bold text-lg transition-all duration-300 hover:shadow-xl hover:shadow-gray-900/10 dark:hover:shadow-white/10 hover:-translate-y-0.5">
                <MessageSquare class="mr-3 h-6 w-6 transition-transform duration-300 group-hover:scale-110" />
                {{ $t('chatbot.hero.cta') }}
              </router-link>
              <router-link to="/chatbot-ticino" class="group inline-flex items-center h-14 px-8 border border-gray-200 dark:border-white/[0.08] text-gray-500 dark:text-gray-400 rounded-xl font-bold text-lg transition-all duration-300 hover:border-gray-300 dark:hover:border-white/[0.15] hover:bg-gray-50 dark:hover:bg-white/[0.03]">
                <Bot class="mr-2 h-5 w-5" />
                {{ $t('chatbotTicino.hero.learnMore') || 'Chatbot Ticino' }}
              </router-link>
            </div>
            <div class="flex flex-wrap gap-6 text-sm text-gray-500 dark:text-gray-400 anim-el d5">
              <div class="flex items-center gap-2"><div class="w-6 h-6 rounded-md bg-blue-500/10 flex items-center justify-center"><Check class="h-3.5 w-3.5 text-blue-500" /></div><span>{{ tr('24/7 Disponibile', '24/7 available') }}</span></div>
              <div class="flex items-center gap-2"><div class="w-6 h-6 rounded-md bg-blue-500/10 flex items-center justify-center"><Check class="h-3.5 w-3.5 text-blue-500" /></div><span>{{ tr('Multilingue', 'Multilingual') }}</span></div>
              <div class="flex items-center gap-2"><div class="w-6 h-6 rounded-md bg-blue-500/10 flex items-center justify-center"><Check class="h-3.5 w-3.5 text-blue-500" /></div><span>{{ tr('Personalizzabile', 'Customizable') }}</span></div>
            </div>
          </div>

          <!-- 3D Chat Mockup with parallax -->
          <div ref="heroMockup" class="relative hidden lg:block" data-animate="hero" :class="{ 'is-visible': visible.hero }" @mousemove="onHeroMove" @mouseleave="onHeroLeave">
            <div class="absolute -inset-20 pointer-events-none"><div class="w-72 h-72 rounded-full bg-blue-500/[0.06] blur-3xl absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" :style="{ transform: `translate(calc(-50% + ${mx * 30}px), calc(-50% + ${my * 30}px))` }"></div></div>
            <div class="chat-mockup rounded-2xl border border-gray-200 dark:border-white/[0.08] overflow-hidden shadow-xl bg-white dark:bg-dark transition-colors duration-300" :style="mockupTransform">
              <div class="flex items-center gap-2 px-4 py-2.5 bg-gray-50 dark:bg-white/[0.04] border-b border-gray-200 dark:border-white/[0.06]">
                <div class="flex gap-1.5">
                  <div class="w-2.5 h-2.5 rounded-full bg-red-400/80"></div>
                  <div class="w-2.5 h-2.5 rounded-full bg-yellow-400/80"></div>
                  <div class="w-2.5 h-2.5 rounded-full bg-green-400/80"></div>
                </div>
                <div class="flex-1 h-6 rounded-md bg-white dark:bg-white/[0.06] mx-8 flex items-center px-3">
                  <Lock class="w-3 h-3 text-green-500 mr-1.5" />
                  <span class="text-[11px] text-gray-400 dark:text-gray-500">tuodominio.com/chat</span>
                </div>
              </div>
              <div class="p-5 space-y-3">
                <div class="flex items-center gap-3 mb-2 anim-el d1">
                  <div class="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center"><Bot class="h-4 w-4 text-white" /></div>
                  <div>
                    <p class="text-[11px] font-bold text-gray-900/60 dark:text-white/40">AI Assistant</p>
                    <p class="text-[8px] text-green-500 flex items-center gap-1"><span class="w-1 h-1 rounded-full bg-green-500 animate-pulse" /> Online</p>
                  </div>
                </div>
                <div class="space-y-2.5 anim-el d2">
                  <div class="flex items-start gap-2" :class="chatVisible >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'" style="transition: all 0.5s cubic-bezier(.22,1,.36,1)">
                    <div class="rounded-xl rounded-tl-sm p-2.5 bg-gray-100 dark:bg-white/[0.06] max-w-[80%]">
                      <p class="text-[10px] text-gray-600 dark:text-gray-400">Buongiorno! Come posso aiutarla oggi?</p>
                    </div>
                  </div>
                  <div class="flex justify-end" :class="chatVisible >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'" style="transition: all 0.5s cubic-bezier(.22,1,.36,1)">
                    <div class="rounded-xl rounded-tr-sm p-2.5 bg-blue-500 max-w-[80%]">
                      <p class="text-[10px] text-white">Vorrei informazioni sui prezzi</p>
                    </div>
                  </div>
                  <div class="flex items-start gap-2" :class="chatVisible >= 3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'" style="transition: all 0.5s cubic-bezier(.22,1,.36,1)">
                    <div class="rounded-xl rounded-tl-sm p-2.5 bg-gray-100 dark:bg-white/[0.06] max-w-[85%]">
                      <p class="text-[10px] text-gray-600 dark:text-gray-400">Certo! I nostri piani partono da CHF 490/mese con supporto 24/7. Vuole una consulenza gratuita?</p>
                    </div>
                  </div>
                  <div v-if="chatVisible >= 4" class="flex items-center gap-1 ml-1">
                    <span class="typing-dot w-1.5 h-1.5 rounded-full bg-gray-400 dark:bg-gray-500" style="animation-delay: 0s" />
                    <span class="typing-dot w-1.5 h-1.5 rounded-full bg-gray-400 dark:bg-gray-500" style="animation-delay: 0.2s" />
                    <span class="typing-dot w-1.5 h-1.5 rounded-full bg-gray-400 dark:bg-gray-500" style="animation-delay: 0.4s" />
                  </div>
                </div>
              </div>
            </div>
            <!-- Floating metric badges -->
            <div class="absolute -top-4 -right-4 anim-el d4 floating-badge" :style="badge1Transform">
              <div class="rounded-xl bg-white dark:bg-dark-50 border border-gray-200 dark:border-white/[0.08] shadow-lg px-3 py-2 flex items-center gap-2">
                <div class="w-7 h-7 rounded-full bg-green-500/10 flex items-center justify-center"><Zap class="w-3.5 h-3.5 text-green-500" /></div>
                <div><div class="text-[10px] text-gray-400">Risposta</div><div class="text-xs font-bold text-gray-900 dark:text-white">&lt; 1 sec</div></div>
              </div>
            </div>
            <div class="absolute -bottom-4 -left-4 anim-el d5 floating-badge" :style="badge2Transform">
              <div class="rounded-xl bg-white dark:bg-dark-50 border border-gray-200 dark:border-white/[0.08] shadow-lg px-3 py-2 flex items-center gap-2">
                <div class="w-7 h-7 rounded-full bg-blue-500/10 flex items-center justify-center"><Globe class="w-3.5 h-3.5 text-blue-500" /></div>
                <div><div class="text-[10px] text-gray-400">Lingue</div><div class="text-xs font-bold text-gray-900 dark:text-white">IT/DE/FR/EN</div></div>
              </div>
            </div>
            <div class="absolute top-1/2 -right-6 anim-el d6 floating-badge" :style="badge3Transform">
              <div class="rounded-xl bg-white dark:bg-dark-50 border border-gray-200 dark:border-white/[0.08] shadow-lg px-3 py-2 flex items-center gap-2">
                <div class="w-7 h-7 rounded-full bg-purple-500/10 flex items-center justify-center"><Shield class="w-3.5 h-3.5 text-purple-500" /></div>
                <div><div class="text-[10px] text-gray-400">GDPR</div><div class="text-xs font-bold text-gray-900 dark:text-white">Compliant</div></div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white dark:from-dark to-transparent z-10"></div>
    </section>

    <!-- Conversational Intelligence (distinct from website style) -->
    <section class="relative py-28 bg-gray-950 overflow-hidden" data-animate="valueprop" :class="{ 'is-visible': visible.valueprop }">
      <div class="absolute inset-0 pointer-events-none">
        <div class="particle" style="left:8%; animation-duration:14s; animation-delay:0s"></div>
        <div class="particle" style="left:22%; animation-duration:18s; animation-delay:3s"></div>
        <div class="particle" style="left:40%; animation-duration:16s; animation-delay:1s"></div>
        <div class="particle" style="left:55%; animation-duration:20s; animation-delay:5s"></div>
        <div class="particle" style="left:72%; animation-duration:15s; animation-delay:2s"></div>
        <div class="particle" style="left:88%; animation-duration:17s; animation-delay:4s"></div>
      </div>

      <div class="container mx-auto px-4 relative z-10">
        <div class="max-w-4xl mx-auto text-center mb-16">
          <h2 class="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">{{ $t('chatbot.whatYouNeed.title') }}</h2>
          <p class="text-lg md:text-xl text-white/80 leading-relaxed">{{ $t('chatbot.whatYouNeed.subtitle') }}</p>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl mx-auto mb-16">
          <div class="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-5 md:p-6 anim-el d1">
            <div class="flex items-center justify-between mb-5">
              <h3 class="text-white font-bold text-lg">Conversation Intelligence</h3>
              <div class="inline-flex items-center gap-1 rounded-full px-2.5 py-1 bg-emerald-500/10 border border-emerald-500/20">
                <span class="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                <span class="text-[10px] text-emerald-300 font-medium">Live</span>
              </div>
            </div>

            <div class="space-y-3">
              <div class="rounded-xl border border-white/[0.06] bg-black/20 p-3">
                <p class="text-[11px] text-white/35 mb-1">Cliente</p>
                <p class="text-sm text-white/90">"Mi serve un preventivo per il piano Business."</p>
              </div>
              <div class="rounded-xl border border-blue-500/20 bg-blue-500/10 p-3">
                <p class="text-[11px] text-blue-200/80 mb-1">AI · Intent Detection</p>
                <p class="text-sm text-white/90">Intent: <span class="font-semibold">Pricing Request</span> · Lingua: IT · Urgenza: Alta</p>
              </div>
              <div class="rounded-xl border border-white/[0.06] bg-black/20 p-3">
                <p class="text-[11px] text-white/35 mb-1">Risposta generata (0.8s)</p>
                <p class="text-sm text-white/90">"Il piano Business parte da <span class="font-semibold text-blue-300">CHF 890/mese</span>. Vuole una demo gratuita?"</p>
              </div>
            </div>
          </div>

          <div class="space-y-4 anim-el d2">
            <div class="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-5">
              <div class="grid grid-cols-3 gap-3">
                <div class="rounded-lg bg-white/[0.04] border border-white/[0.06] p-3">
                  <div class="text-[10px] text-white/40 mb-1">Conversazioni</div>
                  <div class="text-lg font-bold text-white tabular-nums">{{ cConversations.toLocaleString('it-IT') }}</div>
                </div>
                <div class="rounded-lg bg-white/[0.04] border border-white/[0.06] p-3">
                  <div class="text-[10px] text-white/40 mb-1">Soddisfazione</div>
                  <div class="text-lg font-bold text-white tabular-nums">{{ cSatisfaction.toFixed(1) }}%</div>
                </div>
                <div class="rounded-lg bg-white/[0.04] border border-white/[0.06] p-3">
                  <div class="text-[10px] text-white/40 mb-1">Risparmiato</div>
                  <div class="text-lg font-bold text-white tabular-nums">CHF {{ cSaved.toFixed(1) }}k</div>
                </div>
              </div>
            </div>

            <div class="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-5">
              <div class="space-y-4">
                <div>
                  <div class="flex items-center justify-between mb-1.5">
                    <span class="text-[11px] text-white/50">Accuratezza risposte</span>
                    <span class="text-[11px] text-white/80 tabular-nums">{{ cSatisfaction.toFixed(1) }}%</span>
                  </div>
                  <div class="h-2 rounded-full bg-white/10 overflow-hidden">
                    <div class="h-full rounded-full bg-gradient-to-r from-blue-400 to-blue-500 transition-all duration-700" :style="{ width: `${Math.min(cSatisfaction, 100)}%` }"></div>
                  </div>
                </div>
                <div>
                  <div class="flex items-center justify-between mb-1.5">
                    <span class="text-[11px] text-white/50">Risoluzione automatica</span>
                    <span class="text-[11px] text-white/80">85%</span>
                  </div>
                  <div class="h-2 rounded-full bg-white/10 overflow-hidden">
                    <div class="h-full rounded-full bg-gradient-to-r from-emerald-400 to-emerald-500 transition-all duration-700" :style="{ width: visible.valueprop ? '85%' : '0%' }"></div>
                  </div>
                </div>
                <div>
                  <div class="flex items-center justify-between mb-1.5">
                    <span class="text-[11px] text-white/50">Escalation a operatore</span>
                    <span class="text-[11px] text-white/80">15%</span>
                  </div>
                  <div class="h-2 rounded-full bg-white/10 overflow-hidden">
                    <div class="h-full rounded-full bg-gradient-to-r from-violet-400 to-violet-500 transition-all duration-700" :style="{ width: visible.valueprop ? '15%' : '0%' }"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Stats row -->
        <div class="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
          <div v-for="(stat, index) in impressiveStats" :key="index" class="text-center anim-el" :class="'d' + (index + 3)">
            <div class="text-4xl md:text-5xl font-extrabold text-white mb-2">{{ stat.value }}</div>
            <div class="text-sm font-semibold text-white mb-1">{{ stat.label }}</div>
            <div class="text-xs text-white/60">{{ stat.description }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- Problems vs Solutions + Before/After Chat mockups -->
    <section class="py-28 bg-gray-50 dark:bg-dark transition-colors duration-300" data-animate="problems" :class="{ 'is-visible': visible.problems }">
      <div class="container mx-auto px-4">
        <div class="max-w-3xl mx-auto text-center mb-16">
          <p class="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 font-medium mb-4">{{ $t('chatbot.whatIsChatbot.title') }}</p>
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4 transition-colors duration-300">{{ $t('chatbot.whatIsChatbot.title') }}</h2>
          <p class="text-lg text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ $t('chatbot.whatIsChatbot.subtitle') }}</p>
        </div>

        <!-- Before / After chat mockups -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-16">
          <div class="anim-el d1">
            <div class="text-center mb-3"><span class="text-sm font-bold uppercase tracking-wider text-red-500">Senza chatbot</span></div>
            <div class="rounded-xl border border-red-200 dark:border-red-500/25 overflow-hidden bg-white dark:bg-dark transition-colors duration-300 glitch-shake shadow-sm">
              <div class="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-white/[0.04] border-b border-gray-200 dark:border-white/[0.06]">
                <div class="flex gap-1"><div class="w-2 h-2 rounded-full bg-red-400/60"></div><div class="w-2 h-2 rounded-full bg-yellow-400/60"></div><div class="w-2 h-2 rounded-full bg-green-400/60"></div></div>
                <div class="flex-1 h-6 rounded bg-white dark:bg-white/[0.06] mx-4 flex items-center px-2"><span class="text-xs font-medium text-red-500">Supporto chiuso</span></div>
              </div>
              <div class="p-4 space-y-3.5">
                <div class="h-1.5 rounded-full bg-gray-100 dark:bg-white/[0.05] overflow-hidden"><div class="h-full w-1/3 bg-red-400/70 rounded-full loading-stuck"></div></div>
                <div class="rounded-lg bg-gray-50 dark:bg-white/[0.04] border border-dashed border-gray-300 dark:border-white/[0.08] p-3">
                  <p class="text-xs text-gray-600 dark:text-gray-300 mb-1.5">Hai bisogno di aiuto? Compila il modulo.</p>
                  <p class="text-[11px] text-gray-500 dark:text-gray-400">Risponderemo entro 24-48 ore lavorative.</p>
                </div>
                <div class="flex items-center gap-2 text-[11px] text-red-500/90 font-medium">
                  <Clock class="w-3 h-3" /> Tempo medio risposta: 18 ore
                </div>
                <div class="flex items-center gap-2 text-[11px] text-red-500/90 font-medium">
                  <Users class="w-3 h-3" /> 3 clienti persi questa settimana
                </div>
                <div class="h-7 w-24 rounded bg-gray-200 dark:bg-white/[0.08] flex items-center justify-center"><span class="text-[10px] text-gray-500 dark:text-gray-300 font-medium">Invia richiesta</span></div>
              </div>
            </div>
          </div>
          <div class="anim-el d2">
            <div class="text-center mb-3"><span class="text-sm font-bold uppercase tracking-wider text-green-500">Con chatbot AI</span></div>
            <div class="rounded-xl border border-green-200 dark:border-green-500/25 overflow-hidden bg-white dark:bg-dark transition-colors duration-300 after-glow shadow-sm">
              <div class="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-white/[0.04] border-b border-gray-200 dark:border-white/[0.06]">
                <div class="flex gap-1"><div class="w-2 h-2 rounded-full bg-red-400/60"></div><div class="w-2 h-2 rounded-full bg-yellow-400/60"></div><div class="w-2 h-2 rounded-full bg-green-400/60"></div></div>
                <div class="flex-1 h-6 rounded bg-white dark:bg-white/[0.06] mx-4 flex items-center px-2"><Lock class="w-2.5 h-2.5 text-green-500 mr-1.5" /><span class="text-xs text-gray-500 dark:text-gray-400">tuodominio.com</span></div>
              </div>
              <div class="p-4 space-y-3">
                <div class="h-1.5 rounded-full bg-gray-100 dark:bg-white/[0.05] overflow-hidden"><div class="h-full w-full bg-green-500 rounded-full loading-done"></div></div>
                <div class="flex items-start gap-2">
                  <div class="w-6 h-6 rounded-full bg-blue-500/10 flex items-center justify-center flex-shrink-0"><Bot class="w-3 h-3 text-blue-500" /></div>
                  <div class="rounded-lg bg-gray-50 dark:bg-white/[0.04] border border-gray-100 dark:border-white/[0.04] p-2.5 max-w-[82%]"><p class="text-[11px] text-gray-700 dark:text-gray-200 leading-relaxed">Ciao! Sono il tuo assistente AI. Come posso aiutarti?</p></div>
                </div>
                <div class="flex justify-end"><div class="rounded-lg bg-blue-500/90 p-2.5 max-w-[72%]"><p class="text-[11px] text-white leading-relaxed">Quanto costa il piano business?</p></div></div>
                <div class="flex items-start gap-2">
                  <div class="w-6 h-6 rounded-full bg-blue-500/10 flex items-center justify-center flex-shrink-0"><Bot class="w-3 h-3 text-blue-500" /></div>
                  <div class="rounded-lg bg-gray-50 dark:bg-white/[0.04] border border-gray-100 dark:border-white/[0.04] p-2.5 max-w-[88%]"><p class="text-[11px] text-gray-700 dark:text-gray-200 leading-relaxed">Il piano Business parte da CHF 890/mese e include assistenza 24/7, integrazione CRM e analytics avanzati. Vuole fissare una demo?</p></div>
                </div>
                <div class="flex items-center gap-3 text-[11px] font-medium">
                  <span class="text-green-500 flex items-center gap-1"><Zap class="w-3 h-3" /> Risposta: 0.8s</span>
                  <span class="text-blue-500 flex items-center gap-1"><Check class="w-3 h-3" /> 95% soddisfazione</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Problems / Solutions columns -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-5xl mx-auto">
          <div>
            <div class="flex items-center gap-2 mb-8">
              <div class="h-px flex-1 bg-gray-200 dark:bg-white/[0.08]"></div>
              <span class="text-[13px] uppercase tracking-[0.15em] text-gray-500 dark:text-gray-400 font-medium">{{ $t('chatbot.whatIsChatbot.problems.title') }}</span>
              <div class="h-px flex-1 bg-gray-200 dark:bg-white/[0.08]"></div>
            </div>
            <div class="space-y-4">
              <div class="flex items-start gap-3 anim-el d3"><span class="text-red-400 text-xl font-bold flex-shrink-0">&times;</span><div><p class="font-medium text-gray-900 dark:text-white transition-colors duration-300">{{ $t('chatbot.whatIsChatbot.problems.waiting.title') }}</p><p class="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ $t('chatbot.whatIsChatbot.problems.waiting.description') }}</p></div></div>
              <div class="flex items-start gap-3 anim-el d4"><span class="text-red-400 text-xl font-bold flex-shrink-0">&times;</span><div><p class="font-medium text-gray-900 dark:text-white transition-colors duration-300">{{ $t('chatbot.whatIsChatbot.problems.language.title') }}</p><p class="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ $t('chatbot.whatIsChatbot.problems.language.description') }}</p></div></div>
              <div class="flex items-start gap-3 anim-el d5"><span class="text-red-400 text-xl font-bold flex-shrink-0">&times;</span><div><p class="font-medium text-gray-900 dark:text-white transition-colors duration-300">{{ $t('chatbot.whatIsChatbot.problems.costs.title') }}</p><p class="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ $t('chatbot.whatIsChatbot.problems.costs.description') }}</p></div></div>
            </div>
          </div>
          <div>
            <div class="flex items-center gap-2 mb-8">
              <div class="h-px flex-1 bg-blue-200 dark:bg-blue-500/20"></div>
              <span class="text-[13px] uppercase tracking-[0.15em] text-blue-500 font-medium">{{ $t('chatbot.whatIsChatbot.solutions.title') }}</span>
              <div class="h-px flex-1 bg-blue-200 dark:bg-blue-500/20"></div>
            </div>
            <div class="space-y-4">
              <div class="flex items-start gap-3 anim-el d3"><Check class="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" /><div><p class="font-medium text-gray-900 dark:text-white transition-colors duration-300">{{ $t('chatbot.whatIsChatbot.solutions.immediate.title') }}</p><p class="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ $t('chatbot.whatIsChatbot.solutions.immediate.description') }}</p></div></div>
              <div class="flex items-start gap-3 anim-el d4"><Check class="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" /><div><p class="font-medium text-gray-900 dark:text-white transition-colors duration-300">{{ $t('chatbot.whatIsChatbot.solutions.multilingual.title') }}</p><p class="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ $t('chatbot.whatIsChatbot.solutions.multilingual.description') }}</p></div></div>
              <div class="flex items-start gap-3 anim-el d5"><Check class="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" /><div><p class="font-medium text-gray-900 dark:text-white transition-colors duration-300">{{ $t('chatbot.whatIsChatbot.solutions.costEffective.title') }}</p><p class="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ $t('chatbot.whatIsChatbot.solutions.costEffective.description') }}</p></div></div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Features: 2-column with phone mockup + feature cards -->
    <section class="py-28 bg-white dark:bg-dark transition-colors duration-300" data-animate="features" :class="{ 'is-visible': visible.features }">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <p class="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 font-medium mb-4">{{ $t('chatbot.features.title') }}</p>
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6 transition-colors duration-300">{{ $t('chatbot.features.title') }}</h2>
          <p class="text-lg text-gray-500 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed transition-colors duration-300">{{ $t('chatbot.features.subtitle') }}</p>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto mb-16">
          <div class="anim-el d1 flex justify-center">
            <div ref="featurePhone" class="relative" @mousemove="onPhoneMove" @mouseleave="onPhoneLeave">
              <div class="phone-mockup rounded-[2rem] border-4 border-gray-800 dark:border-gray-700 overflow-hidden shadow-2xl bg-white dark:bg-dark w-[260px] h-[520px] transition-colors duration-300" :style="phoneTransform">
                <div class="h-6 bg-gray-800 dark:bg-gray-700 flex items-center justify-center rounded-b-xl mx-12 mb-2"><div class="w-12 h-1 rounded-full bg-gray-600"></div></div>
                <div class="p-3">
                  <div class="flex items-center justify-between mb-3">
                    <div class="flex items-center gap-2">
                      <div class="w-6 h-6 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center"><Bot class="h-3 w-3 text-white" /></div>
                      <div><p class="text-[9px] font-bold text-gray-800 dark:text-white">AI Assistant</p><p class="text-[7px] text-green-500">Online</p></div>
                    </div>
                    <div class="flex gap-1"><div class="w-5 h-5 rounded-full bg-gray-100 dark:bg-white/[0.06] flex items-center justify-center"><Phone class="w-2.5 h-2.5 text-gray-400" /></div><div class="w-5 h-5 rounded-full bg-gray-100 dark:bg-white/[0.06] flex items-center justify-center"><Settings class="w-2.5 h-2.5 text-gray-400" /></div></div>
                  </div>
                  <div class="space-y-2" :class="{ 'phone-chat-revealed': phoneRevealed }">
                    <div class="phone-msg phone-msg-1 flex items-start gap-1.5">
                      <div class="w-5 h-5 rounded-full bg-blue-500/10 flex items-center justify-center flex-shrink-0"><Bot class="w-2.5 h-2.5 text-blue-500" /></div>
                      <div class="rounded-lg rounded-tl-sm bg-gray-50 dark:bg-white/[0.04] p-2 max-w-[85%]"><p class="text-[8px] text-gray-600 dark:text-gray-300">Ciao! Come posso aiutarti oggi?</p></div>
                    </div>
                    <div class="phone-msg phone-msg-2 flex justify-end"><div class="rounded-lg rounded-tr-sm bg-blue-500 p-2 max-w-[80%]"><p class="text-[8px] text-white">Cerco info sui prezzi</p></div></div>
                    <div class="phone-msg phone-msg-3 flex items-start gap-1.5">
                      <div class="w-5 h-5 rounded-full bg-blue-500/10 flex items-center justify-center flex-shrink-0"><Bot class="w-2.5 h-2.5 text-blue-500" /></div>
                      <div class="rounded-lg rounded-tl-sm bg-gray-50 dark:bg-white/[0.04] p-2 max-w-[85%]">
                        <p class="text-[8px] text-gray-600 dark:text-gray-300 mb-2">Ecco i nostri piani:</p>
                        <div class="space-y-1">
                          <div class="rounded bg-white dark:bg-white/[0.04] border border-gray-100 dark:border-white/[0.06] p-1.5 flex items-center justify-between">
                            <span class="text-[7px] text-gray-600 dark:text-gray-300 font-bold">Starter</span><span class="text-[7px] text-blue-500 font-bold">CHF 490</span>
                          </div>
                          <div class="rounded bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 p-1.5 flex items-center justify-between">
                            <span class="text-[7px] text-blue-600 dark:text-blue-300 font-bold">Business</span><span class="text-[7px] text-blue-500 font-bold">CHF 890</span>
                          </div>
                          <div class="rounded bg-white dark:bg-white/[0.04] border border-gray-100 dark:border-white/[0.06] p-1.5 flex items-center justify-between">
                            <span class="text-[7px] text-gray-600 dark:text-gray-300 font-bold">Enterprise</span><span class="text-[7px] text-blue-500 font-bold">Custom</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="phone-msg phone-msg-4 flex justify-end"><div class="rounded-lg rounded-tr-sm bg-blue-500 p-2 max-w-[80%]"><p class="text-[8px] text-white">Business sembra perfetto!</p></div></div>
                    <div class="phone-msg phone-msg-5 flex items-start gap-1.5">
                      <div class="w-5 h-5 rounded-full bg-blue-500/10 flex items-center justify-center flex-shrink-0"><Bot class="w-2.5 h-2.5 text-blue-500" /></div>
                      <div class="rounded-lg rounded-tl-sm bg-gray-50 dark:bg-white/[0.04] p-2 max-w-[85%]">
                        <p class="text-[8px] text-gray-600 dark:text-gray-300">Ottimo! Prenoto una demo gratuita?</p>
                        <div class="flex gap-1 mt-1.5">
                          <div class="rounded bg-blue-500 px-2 py-0.5"><span class="text-[6px] text-white font-bold">Si, prenota</span></div>
                          <div class="rounded bg-gray-100 dark:bg-white/[0.06] px-2 py-0.5"><span class="text-[6px] text-gray-500 dark:text-gray-400 font-bold">Più info</span></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="mt-3 flex items-center gap-2">
                    <div class="flex-1 h-7 rounded-full bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.06] flex items-center px-3"><span class="text-[8px] text-gray-300 dark:text-gray-600">Scrivi...</span></div>
                    <div class="h-7 w-7 rounded-full bg-blue-500 flex items-center justify-center"><MessageSquare class="w-3 h-3 text-white" /></div>
                  </div>
                </div>
              </div>
              <div class="absolute -top-3 -right-6 phone-badge phone-badge-1 floating-badge" :style="phoneBadge1">
                <div class="rounded-lg bg-white dark:bg-dark-50 border border-gray-200 dark:border-white/[0.08] shadow-lg px-2.5 py-1.5 flex items-center gap-1.5">
                  <Zap class="w-3 h-3 text-yellow-500" /><span class="text-[9px] font-bold text-gray-700 dark:text-gray-300">0.8s</span>
                </div>
              </div>
              <div class="absolute -bottom-3 -left-6 phone-badge phone-badge-2 floating-badge" :style="phoneBadge2">
                <div class="rounded-lg bg-white dark:bg-dark-50 border border-gray-200 dark:border-white/[0.08] shadow-lg px-2.5 py-1.5 flex items-center gap-1.5">
                  <Globe class="w-3 h-3 text-blue-500" /><span class="text-[9px] font-bold text-gray-700 dark:text-gray-300">4 lingue</span>
                </div>
              </div>
              <div class="absolute top-1/3 -left-8 phone-badge phone-badge-3 floating-badge" :style="phoneBadge3">
                <div class="rounded-lg bg-white dark:bg-dark-50 border border-gray-200 dark:border-white/[0.08] shadow-lg px-2.5 py-1.5 flex items-center gap-1.5">
                  <Shield class="w-3 h-3 text-green-500" /><span class="text-[9px] font-bold text-gray-700 dark:text-gray-300">GDPR</span>
                </div>
              </div>
            </div>
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div v-for="(feature, idx) in features" :key="feature.title" class="group rounded-xl p-5 bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] transition-all duration-300 hover:border-blue-200 dark:hover:border-blue-500/20 hover:shadow-md hover:shadow-blue-500/5 hover:-translate-y-0.5 anim-el" :class="'d' + (idx + 2)">
              <div class="w-9 h-9 rounded-lg flex items-center justify-center mb-3 transition-all duration-300 group-hover:scale-110" :class="feature.bg || 'bg-gray-100 dark:bg-white/[0.06] border border-gray-200 dark:border-white/[0.06]'">
                <component :is="feature.icon" class="h-4.5 w-4.5" :class="feature.iconColor || 'text-gray-500 dark:text-gray-400'" />
              </div>
              <h3 class="text-base font-bold mb-1.5 text-gray-900 dark:text-white transition-colors duration-300">{{ feature.title }}</h3>
              <p class="text-xs text-gray-500 dark:text-gray-400 leading-relaxed transition-colors duration-300">{{ feature.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Process (timeline with step illustrations) -->
    <section class="py-28 bg-gray-50 dark:bg-dark transition-colors duration-300" data-animate="process" :class="{ 'is-visible': visible.process }">
      <div class="container mx-auto px-4">
        <div class="max-w-3xl mx-auto text-center mb-20">
          <p class="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 font-medium mb-4">{{ $t('chatbot.development.title') }}</p>
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4 transition-colors duration-300">{{ $t('chatbot.development.title') }}</h2>
          <p class="text-lg text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ $t('chatbot.development.subtitle') }}</p>
        </div>
        <div class="max-w-3xl mx-auto space-y-0">
          <div v-for="(step, index) in developmentSteps" :key="step.title" class="relative flex gap-6 md:gap-10 anim-el" :class="'d' + (index + 1)">
            <div class="flex flex-col items-center">
              <div class="w-12 h-12 rounded-full bg-gray-100 dark:bg-white/[0.06] border border-gray-200 dark:border-white/[0.1] flex items-center justify-center text-sm font-bold text-gray-900 dark:text-white flex-shrink-0 transition-colors duration-300">{{ index + 1 }}</div>
              <div v-if="index < developmentSteps.length - 1" class="w-px flex-1 bg-gray-200 dark:bg-white/[0.06] my-2 transition-colors duration-300"></div>
            </div>
            <div class="pb-14 flex-1" :class="{ 'pb-0': index === developmentSteps.length - 1 }">
              <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-2 transition-colors duration-300">{{ step.title }}</h3>
              <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed mb-4 transition-colors duration-300">{{ step.description }}</p>

              <!-- Step 1: Animated checklist -->
              <div v-if="index === 0" class="rounded-lg border border-gray-200 dark:border-white/[0.06] bg-white dark:bg-white/[0.02] p-3 max-w-xs transition-colors duration-300">
                <div class="space-y-2">
                  <div v-for="(chk, ci) in ['Analisi obiettivi', 'Target audience', 'Flusso conversazione', 'Knowledge base']" :key="ci" class="flex items-center gap-2 transition-all duration-500" :class="checksVisible > ci ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-3'">
                    <div class="w-4 h-4 rounded flex items-center justify-center transition-colors duration-300" :class="checksVisible > ci ? 'border-blue-500 bg-blue-500/10 border' : 'border border-gray-300 dark:border-white/[0.1]'"><Check v-if="checksVisible > ci" class="w-2.5 h-2.5 text-blue-500" /></div>
                    <span class="text-[9px] text-gray-500 dark:text-gray-400">{{ chk }}</span>
                  </div>
                </div>
              </div>

              <!-- Step 2: Chatbot flow builder -->
              <div v-if="index === 1" class="rounded-lg border border-gray-200 dark:border-white/[0.06] bg-white dark:bg-white/[0.02] p-3 max-w-xs transition-colors duration-300">
                <p class="text-[8px] text-gray-400 dark:text-gray-500 mb-2">Flusso conversazione</p>
                <div class="flex flex-col items-center gap-1">
                  <div class="w-full h-6 rounded bg-blue-500/10 border border-blue-500/20 flex items-center justify-center"><span class="text-[7px] text-blue-500 font-bold">Benvenuto</span></div>
                  <div class="w-px h-3 bg-gray-200 dark:bg-white/[0.1]"></div>
                  <div class="flex gap-2 w-full">
                    <div class="flex-1 h-6 rounded bg-green-500/10 border border-green-500/20 flex items-center justify-center"><span class="text-[6px] text-green-500 font-bold">Prezzi</span></div>
                    <div class="flex-1 h-6 rounded bg-purple-500/10 border border-purple-500/20 flex items-center justify-center"><span class="text-[6px] text-purple-500 font-bold">Supporto</span></div>
                    <div class="flex-1 h-6 rounded bg-orange-500/10 border border-orange-500/20 flex items-center justify-center"><span class="text-[6px] text-orange-500 font-bold">FAQ</span></div>
                  </div>
                  <div class="w-px h-3 bg-gray-200 dark:bg-white/[0.1]"></div>
                  <div class="w-full h-6 rounded bg-gray-100 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.06] flex items-center justify-center"><span class="text-[7px] text-gray-400 font-bold">Azione / CTA</span></div>
                </div>
              </div>

              <!-- Step 3: Code editor with typing -->
              <div v-if="index === 2" class="rounded-lg border border-gray-200 dark:border-white/[0.06] bg-gray-900 dark:bg-black/40 p-3 max-w-xs font-mono text-[10px] leading-relaxed overflow-hidden transition-colors duration-300">
                <div class="flex gap-1 mb-2"><div class="w-2 h-2 rounded-full bg-red-400/60"></div><div class="w-2 h-2 rounded-full bg-yellow-400/60"></div><div class="w-2 h-2 rounded-full bg-green-400/60"></div></div>
                <div v-if="codeVisible >= 1" class="code-line"><span class="text-purple-400">const</span> <span class="text-blue-300">chatbot</span> <span class="text-gray-500">=</span> <span class="text-green-400">new AI()</span><span v-if="codeVisible === 1" class="typing-cursor">|</span></div>
                <div v-if="codeVisible >= 2" class="code-line"><span class="text-blue-300">chatbot</span><span class="text-gray-500">.</span><span class="text-yellow-300">train</span><span class="text-gray-500">(</span><span class="text-green-400">knowledge</span><span class="text-gray-500">)</span><span v-if="codeVisible === 2" class="typing-cursor">|</span></div>
                <div v-if="codeVisible >= 3" class="code-line"><span class="text-blue-300">chatbot</span><span class="text-gray-500">.</span><span class="text-yellow-300">deploy</span><span class="text-gray-500">(</span><span class="text-green-400">'production'</span><span class="text-gray-500">)</span><span v-if="codeVisible === 3" class="typing-cursor">|</span></div>
                <div v-if="codeVisible >= 4" class="code-line"><span class="text-gray-500">// </span><span class="text-green-300">Ready! 🚀</span><span class="typing-cursor">|</span></div>
                <div v-if="codeVisible === 0" class="text-gray-600">_<span class="typing-cursor">|</span></div>
              </div>

              <!-- Step 4: Launch -->
              <div v-if="index === 3" class="rounded-lg border border-gray-200 dark:border-white/[0.06] bg-white dark:bg-white/[0.02] p-3 max-w-xs transition-colors duration-300">
                <div class="flex items-center gap-3">
                  <div class="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center flex-shrink-0"><Bot class="w-5 h-5 text-green-500" /></div>
                  <div>
                    <p class="text-[9px] font-bold text-green-600 dark:text-green-400 mb-0.5">Chatbot Online!</p>
                    <p class="text-[7px] text-gray-400 dark:text-gray-500">Performance in crescita</p>
                  </div>
                  <div class="ml-auto flex gap-0.5">
                    <div class="w-1.5 h-3 rounded-sm bg-green-500/40"></div>
                    <div class="w-1.5 h-5 rounded-sm bg-green-500/60"></div>
                    <div class="w-1.5 h-7 rounded-sm bg-green-500/80"></div>
                    <div class="w-1.5 h-9 rounded-sm bg-green-500"></div>
                  </div>
                </div>
              </div>

              <ul class="text-sm text-gray-500 dark:text-gray-400 space-y-2 mt-4">
                <li v-for="detail in step.details" :key="detail" class="flex items-start"><Check class="h-4 w-4 text-blue-500 mr-2 flex-shrink-0 mt-0.5" /><span>{{ detail }}</span></li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Use Cases -->
    <section class="py-28 bg-white dark:bg-dark transition-colors duration-300" data-animate="usecases" :class="{ 'is-visible': visible.usecases }">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <p class="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 font-medium mb-4">{{ $t('chatbot.useCases.title') }}</p>
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6 transition-colors duration-300">{{ $t('chatbot.useCases.title') }}</h2>
          <p class="text-lg text-gray-500 dark:text-gray-400 max-w-3xl mx-auto transition-colors duration-300">{{ $t('chatbot.useCases.subtitle') }}</p>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
          <div v-for="(uc, idx) in useCases" :key="uc.title" class="group rounded-2xl p-6 bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] transition-all duration-300 hover:border-blue-200 dark:hover:border-blue-500/20 hover:shadow-lg hover:shadow-blue-500/5 hover:-translate-y-1 anim-el text-center" :class="'d' + (idx + 1)">
            <div class="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-5 transition-all duration-300 group-hover:scale-110" :class="uc.bg">
              <component :is="uc.icon" class="h-6 w-6" :class="uc.iconColor" />
            </div>
            <h3 class="text-lg font-bold mb-2 text-gray-900 dark:text-white transition-colors duration-300">{{ uc.title }}</h3>
            <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed transition-colors duration-300">{{ uc.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Industries: Interactive Showcase with Chat Previews -->
    <section class="py-28 bg-gray-50 dark:bg-dark transition-colors duration-300" data-animate="industries" :class="{ 'is-visible': visible.industries }">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <p class="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 font-medium mb-4">{{ $t('chatbot.industries.title') }}</p>
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4 transition-colors duration-300">{{ $t('chatbot.industries.title') }}</h2>
          <p class="text-lg text-gray-500 dark:text-gray-400 max-w-3xl mx-auto transition-colors duration-300">{{ $t('chatbot.industries.subtitle') }}</p>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-5 gap-6 max-w-6xl mx-auto">
          <div class="lg:col-span-2 space-y-2 anim-el d1">
            <button
              v-for="(industry, idx) in industries"
              :key="industry.title"
              @click="activeIndustry = idx"
              class="w-full text-left rounded-xl p-4 border transition-all duration-300"
              :class="activeIndustry === idx
                ? 'bg-white dark:bg-white/[0.06] border-gray-300 dark:border-white/[0.12] shadow-sm'
                : 'bg-transparent border-transparent hover:bg-white/60 dark:hover:bg-white/[0.02] hover:border-gray-200 dark:hover:border-white/[0.06]'"
            >
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors duration-300"
                  :class="activeIndustry === idx
                    ? 'bg-blue-500/10 border border-blue-500/20'
                    : 'bg-gray-100 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.06]'">
                  <component :is="industry.icon" class="h-5 w-5 transition-colors duration-300" :class="activeIndustry === idx ? 'text-blue-500' : 'text-gray-400 dark:text-gray-500'" />
                </div>
                <div class="flex-1 min-w-0">
                  <h3 class="text-sm font-bold transition-colors duration-300" :class="activeIndustry === idx ? 'text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400'">{{ industry.title }}</h3>
                  <p v-if="activeIndustry === idx" class="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-relaxed line-clamp-2">{{ industry.description }}</p>
                </div>
                <ChevronDown class="w-4 h-4 -rotate-90 flex-shrink-0 transition-all duration-300" :class="activeIndustry === idx ? 'text-blue-500 rotate-0' : 'text-gray-300 dark:text-gray-600'" />
              </div>
              <div v-if="activeIndustry === idx" class="mt-3 pt-3 border-t border-gray-100 dark:border-white/[0.06]">
                <ul class="space-y-1.5">
                  <li v-for="app in industry.applications" :key="app" class="flex items-center text-xs text-gray-500 dark:text-gray-400">
                    <Check class="h-3 w-3 text-blue-500 mr-2 flex-shrink-0" /><span>{{ app }}</span>
                  </li>
                </ul>
              </div>
            </button>
          </div>

          <div class="lg:col-span-3 anim-el d2">
            <div class="sticky top-24">
              <div class="rounded-2xl border border-gray-200 dark:border-white/[0.08] overflow-hidden bg-white dark:bg-dark shadow-xl transition-colors duration-300">
                <div class="flex items-center gap-2 px-4 py-2.5 bg-gray-50 dark:bg-white/[0.04] border-b border-gray-200 dark:border-white/[0.06]">
                  <div class="flex gap-1.5"><div class="w-2.5 h-2.5 rounded-full bg-red-400/70"></div><div class="w-2.5 h-2.5 rounded-full bg-yellow-400/70"></div><div class="w-2.5 h-2.5 rounded-full bg-green-400/70"></div></div>
                  <div class="flex-1 h-6 rounded-md bg-white dark:bg-white/[0.06] mx-6 flex items-center px-3">
                    <Lock class="w-3 h-3 text-green-500 mr-1.5" />
                    <span class="text-[11px] text-gray-400 dark:text-gray-500">{{ industryChats[activeIndustry]?.domain }}</span>
                  </div>
                </div>
                <div class="relative" style="min-height: 320px">
                  <transition name="preview-fade" mode="out-in">
                    <div :key="activeIndustry" class="p-5">
                      <div class="flex items-center gap-3 mb-4">
                        <div class="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center"><Bot class="h-4 w-4 text-white" /></div>
                        <div><p class="text-[11px] font-bold text-gray-900/60 dark:text-white/40">{{ industryChats[activeIndustry]?.botName }}</p><p class="text-[8px] text-green-500 flex items-center gap-1"><span class="w-1 h-1 rounded-full bg-green-500 animate-pulse" /> Online</p></div>
                      </div>
                      <div class="space-y-2.5">
                        <div v-for="(msg, mi) in industryChats[activeIndustry]?.messages" :key="mi">
                          <div v-if="msg.from === 'bot'" class="flex items-start gap-2">
                            <div class="w-6 h-6 rounded-full bg-blue-500/10 flex items-center justify-center flex-shrink-0"><Bot class="w-3 h-3 text-blue-500" /></div>
                            <div class="rounded-lg bg-gray-50 dark:bg-white/[0.04] border border-gray-100 dark:border-white/[0.04] p-2.5 max-w-[85%]"><p class="text-[9px] text-gray-600 dark:text-gray-300 leading-relaxed">{{ msg.text }}</p></div>
                          </div>
                          <div v-else class="flex justify-end">
                            <div class="rounded-lg bg-blue-500/80 p-2.5 max-w-[75%]"><p class="text-[9px] text-white leading-relaxed">{{ msg.text }}</p></div>
                          </div>
                        </div>
                      </div>
                      <div class="mt-4 pt-3 border-t border-gray-100 dark:border-white/[0.06] flex items-center gap-3">
                        <div class="flex-1 h-8 rounded-lg bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.06] flex items-center px-3"><span class="text-[9px] text-gray-300 dark:text-gray-600">Scrivi un messaggio...</span></div>
                        <div class="h-8 w-8 rounded-lg bg-blue-500 flex items-center justify-center"><MessageSquare class="w-3.5 h-3.5 text-white" /></div>
                      </div>
                    </div>
                  </transition>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Benefits with animated counters + charts -->
    <section class="py-28 bg-white dark:bg-dark transition-colors duration-300" data-animate="benefits" :class="{ 'is-visible': visible.benefits }">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <p class="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 font-medium mb-4">{{ $t('chatbot.benefits.title') }}</p>
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6 transition-colors duration-300">{{ $t('chatbot.benefits.title') }}</h2>
          <p class="text-lg text-gray-500 dark:text-gray-400 max-w-3xl mx-auto transition-colors duration-300">{{ $t('chatbot.benefits.subtitle') }}</p>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
          <div class="space-y-6">
            <div v-for="(benefit, idx) in benefits" :key="benefit.title" class="group flex items-start space-x-4 anim-el" :class="'d' + (idx + 1)">
              <div class="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-300 group-hover:scale-110" :class="benefit.bg">
                <component :is="benefit.icon" class="h-5 w-5" :class="benefit.iconColor" />
              </div>
              <div>
                <h3 class="text-lg font-semibold mb-1 text-gray-900 dark:text-white transition-colors duration-300">{{ benefit.title }}</h3>
                <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed transition-colors duration-300">{{ benefit.description }}</p>
              </div>
            </div>
          </div>

          <div class="anim-el d5">
            <div class="rounded-2xl p-6 bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] transition-colors duration-300">
              <h3 class="text-lg font-bold mb-6 text-gray-900 dark:text-white text-center transition-colors duration-300">{{ $t('chatbot.benefits.stats.title') }}</h3>
              <div class="grid grid-cols-2 gap-4 mb-6">
                <div class="rounded-xl p-4 bg-white dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] text-center transition-colors duration-300">
                  <div class="text-3xl font-bold text-blue-500 mb-1 tabular-nums">{{ cResolution }}%</div>
                  <div class="text-xs text-gray-500 dark:text-gray-400">Risoluzione auto</div>
                </div>
                <div class="rounded-xl p-4 bg-white dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] text-center transition-colors duration-300">
                  <div class="text-3xl font-bold text-green-500 mb-1 tabular-nums">{{ cCostSaving }}%</div>
                  <div class="text-xs text-gray-500 dark:text-gray-400">Costi ridotti</div>
                </div>
                <div class="rounded-xl p-4 bg-white dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] text-center transition-colors duration-300">
                  <div class="text-3xl font-bold text-purple-500 mb-1 tabular-nums">{{ cSatScore.toFixed(1) }}%</div>
                  <div class="text-xs text-gray-500 dark:text-gray-400">Soddisfazione</div>
                </div>
                <div class="rounded-xl p-4 bg-white dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] text-center transition-colors duration-300">
                  <div class="text-3xl font-bold text-orange-500 mb-1 tabular-nums">+{{ cLeads }}%</div>
                  <div class="text-xs text-gray-500 dark:text-gray-400">Lead generati</div>
                </div>
              </div>
              <div class="rounded-xl p-4 bg-white dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] transition-colors duration-300">
                <div class="text-[10px] text-gray-400 dark:text-gray-500 mb-2">Crescita conversazioni mensili</div>
                <div class="flex items-end gap-1.5 h-16">
                  <div v-for="(h, i) in [30,38,45,42,55,52,65,72,80]" :key="i" class="flex-1 rounded-t bar-grow" :class="i >= 6 ? 'bg-blue-500/70' : i >= 4 ? 'bg-blue-500/40' : 'bg-gray-200 dark:bg-white/[0.08]'" :style="{ '--h': h + '%' }" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Integration Platforms -->
    <section class="py-28 bg-gray-50 dark:bg-dark transition-colors duration-300" data-animate="integrations" :class="{ 'is-visible': visible.integrations }">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <p class="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 font-medium mb-4">{{ $t('chatbot.technology.integration.title') }}</p>
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6 transition-colors duration-300">{{ $t('chatbot.technology.integration.title') }}</h2>
          <p class="text-lg text-gray-500 dark:text-gray-400 max-w-3xl mx-auto transition-colors duration-300">{{ $t('chatbot.technology.subtitle') }}</p>
        </div>
        <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 max-w-4xl mx-auto">
          <div v-for="(platform, idx) in integrationPlatforms" :key="platform.name" class="group rounded-2xl p-5 text-center bg-white dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] transition-all duration-300 hover:border-blue-200 dark:hover:border-blue-500/20 hover:shadow-lg hover:shadow-blue-500/5 hover:-translate-y-1 anim-el" :class="'d' + (idx + 1)">
            <component :is="platform.icon" class="h-7 w-7 text-gray-400 dark:text-gray-500 mx-auto mb-3 transition-colors duration-300 group-hover:text-blue-500" />
            <p class="text-xs font-semibold text-gray-700 dark:text-gray-300 transition-colors duration-300">{{ platform.name }}</p>
          </div>
        </div>
        <div class="mt-12 text-center anim-el d7">
          <div class="max-w-2xl mx-auto rounded-2xl p-6 bg-white dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] transition-colors duration-300">
            <div class="grid grid-cols-3 gap-4">
              <div v-for="ai in aiTech" :key="ai.title" class="text-center">
                <div class="w-10 h-10 rounded-lg flex items-center justify-center mx-auto mb-3 transition-colors duration-300" :class="ai.bg">
                  <component :is="ai.icon" class="h-5 w-5" :class="ai.iconColor" />
                </div>
                <h4 class="text-xs font-bold text-gray-900 dark:text-white mb-1 transition-colors duration-300">{{ ai.title }}</h4>
                <p class="text-[10px] text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ ai.desc }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Technology (dark section) -->
    <section class="relative py-28 bg-gray-950 overflow-hidden" data-animate="technology" :class="{ 'is-visible': visible.technology }">
      <div class="absolute inset-0 pointer-events-none">
        <div class="absolute top-0 left-1/4 w-96 h-96 rounded-full bg-blue-500/[0.04] blur-3xl"></div>
        <div class="absolute bottom-0 right-1/4 w-80 h-80 rounded-full bg-purple-500/[0.04] blur-3xl"></div>
      </div>
      <div class="container mx-auto px-4 relative z-10">
        <div class="max-w-4xl mx-auto text-center mb-16">
          <p class="text-[13px] uppercase tracking-[0.2em] text-blue-400/60 font-medium mb-4 anim-el d1">{{ $t('chatbot.techStack.title') }}</p>
          <h2 class="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 anim-el d1">{{ $t('chatbot.techStack.title') }}</h2>
          <p class="text-lg md:text-xl text-white/60 leading-relaxed max-w-2xl mx-auto anim-el d2">{{ $t('chatbot.techStack.subtitle') }}</p>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
          <div v-for="(tech, idx) in techStack" :key="tech.name" class="group rounded-2xl p-6 border border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.05] hover:border-white/[0.12] transition-all duration-500 anim-el" :class="'d' + (idx + 2)">
            <div class="w-12 h-12 rounded-xl flex items-center justify-center mb-5 transition-colors duration-300" :class="tech.bg">
              <component :is="tech.icon" class="h-6 w-6" :class="tech.iconColor" />
            </div>
            <h3 class="text-lg font-bold text-white mb-2">{{ tech.name }}</h3>
            <p class="text-sm text-white/50 leading-relaxed mb-4">{{ tech.description }}</p>
            <ul class="text-sm text-white/40 space-y-1.5">
              <li v-for="feature in tech.features" :key="feature" class="flex items-center"><Check class="h-3.5 w-3.5 text-blue-400 mr-2 flex-shrink-0" />{{ feature }}</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ (2-column: CTA left + accordion right) -->
    <section class="py-28 bg-gray-50 dark:bg-dark transition-colors duration-300" data-animate="faq" :class="{ 'is-visible': visible.faq }">
      <div class="container mx-auto px-4">
        <div class="grid grid-cols-1 lg:grid-cols-5 gap-12 max-w-6xl mx-auto">
          <div class="lg:col-span-2 anim-el d1">
            <div class="lg:sticky lg:top-24">
              <p class="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 font-medium mb-4">{{ $t('chatbot.faq.title') }}</p>
              <h2 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4 transition-colors duration-300">{{ $t('chatbot.faq.title') }}</h2>
              <p class="text-base text-gray-500 dark:text-gray-400 leading-relaxed mb-8 transition-colors duration-300">{{ $t('chatbot.faq.subtitle') }}</p>
              <router-link to="/contact" class="group inline-flex items-center h-12 px-6 bg-gray-900 dark:bg-white text-white dark:text-dark rounded-xl font-bold transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5">
                <MessageSquare class="mr-2 h-5 w-5 transition-transform duration-300 group-hover:scale-110" />{{ $t('chatbot.cta.primary') }}
              </router-link>
            </div>
          </div>
          <div class="lg:col-span-3 anim-el d2">
            <div class="space-y-3">
              <div v-for="(faq, index) in faqs" :key="index" class="rounded-xl border transition-all duration-300"
                :class="activeFaqIndex === index ? 'bg-white dark:bg-white/[0.04] border-gray-300 dark:border-white/[0.12] shadow-sm' : 'bg-transparent border-gray-200 dark:border-white/[0.06] hover:border-gray-300 dark:hover:border-white/[0.1]'">
                <button type="button" @click="toggleFaq(index)" class="w-full flex justify-between items-center p-5 text-left">
                  <h3 class="text-[15px] font-semibold text-gray-900 dark:text-white pr-8 transition-colors duration-300">{{ faq.question }}</h3>
                  <div class="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-300" :class="activeFaqIndex === index ? 'bg-blue-500/10' : 'bg-gray-100 dark:bg-white/[0.06]'">
                    <ChevronDown class="h-4 w-4 flex-shrink-0 transition-all duration-300" :class="activeFaqIndex === index ? 'rotate-180 text-blue-500' : 'text-gray-400 dark:text-gray-500'" />
                  </div>
                </button>
                <div v-show="activeFaqIndex === index" class="px-5 pb-5 -mt-1">
                  <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed transition-colors duration-300">{{ faq.answer }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Final CTA (always dark) -->
    <section class="relative py-28 md:py-40 overflow-hidden" data-animate="cta" :class="{ 'is-visible': visible.cta }">
      <div class="absolute inset-0 bg-gray-950">
        <img src="/ciobai_hero_background.webp" alt="" class="absolute inset-0 w-full h-full object-cover opacity-60" />
      </div>
      <div class="absolute inset-0 pointer-events-none">
        <div class="particle" style="left:12%; animation-duration:16s; animation-delay:1s"></div>
        <div class="particle" style="left:35%; animation-duration:19s; animation-delay:3s"></div>
        <div class="particle" style="left:58%; animation-duration:14s; animation-delay:0s"></div>
        <div class="particle" style="left:80%; animation-duration:17s; animation-delay:5s"></div>
      </div>
      <div class="container mx-auto px-4 text-center relative z-10">
        <div class="max-w-5xl mx-auto">
          <div class="inline-flex items-center gap-2 rounded-full bg-white/[0.06] border border-white/[0.08] px-4 py-1.5 text-[13px] font-medium text-gray-300 mb-6 anim-el d1">{{ $t('chatbot.hero.tagline') }}</div>
          <h2 class="text-4xl md:text-5xl font-extrabold text-white mb-4 anim-el d2">{{ $t('chatbot.cta.title') }}</h2>
          <p class="text-xl md:text-2xl mb-8 text-white/90 font-semibold max-w-3xl mx-auto anim-el d3">{{ $t('chatbot.cta.description') }}</p>
          <div class="flex flex-col sm:flex-row gap-4 justify-center items-center mb-10 anim-el d4">
            <router-link to="/contact" class="group inline-flex items-center h-14 px-8 bg-white text-gray-900 font-bold text-lg rounded-xl transition-all duration-300 hover:shadow-xl hover:shadow-white/10 hover:-translate-y-0.5"><MessageSquare class="mr-3 h-6 w-6 transition-transform duration-300 group-hover:scale-110" />{{ $t('chatbot.cta.primary') }}</router-link>
            <router-link to="/chatbot-ticino" class="group inline-flex items-center h-14 px-8 border border-white/20 text-white rounded-xl font-semibold text-lg transition-all duration-300 hover:border-white/40 hover:bg-white/[0.04]"><Bot class="mr-2 h-5 w-5" />{{ $t('chatbot.cta.ticino') }}</router-link>
          </div>
          <div class="flex flex-wrap justify-center gap-6 text-sm text-white/90 anim-el d5">
            <div class="flex items-center"><Check class="h-5 w-5 text-blue-400 mr-2 flex-shrink-0" />{{ tr('24/7 Disponibile', '24/7 available') }}</div>
            <div class="flex items-center"><Check class="h-5 w-5 text-blue-400 mr-2 flex-shrink-0" />{{ tr('Multilingue', 'Multilingual') }}</div>
            <div class="flex items-center"><Check class="h-5 w-5 text-blue-400 mr-2 flex-shrink-0" />{{ tr('Personalizzabile', 'Customizable') }}</div>
          </div>
          <div class="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto anim-el d6">
            <div v-for="(stat, idx) in ctaStats" :key="idx" class="text-center">
              <div class="text-2xl font-bold text-white mb-1">{{ stat.value }}</div>
              <div class="text-[11px] text-white/50">{{ stat.label }}</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <FloatingCta />
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted, onUnmounted } from 'vue'
import {
  MessageSquare, Bot, Clock, Globe, DollarSign, Zap, Users, Shield,
  HeadphonesIcon, ShoppingCart, BookOpen, TrendingUp, Check, Brain,
  Database, Smartphone, Building, Car, Heart, GraduationCap, CreditCard, Settings,
  Webhook, MessageCircle, Instagram, Facebook, Code, ChevronDown, Lock,
  Phone, Gauge, Target, RefreshCcw, Cpu
} from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'
import { useTheme } from '../composables/useTheme'
import FloatingCta from '../components/FloatingCta.vue'

const { isDark } = useTheme()
const { t, locale } = useI18n()
const tr = (it, en) => ((locale.value || '').startsWith('en') ? en : it)
const activeFaqIndex = ref(null)
const visible = reactive({})
let observer = null

function toggleFaq(index) {
  activeFaqIndex.value = activeFaqIndex.value === index ? null : index
}

const mx = ref(0)
const my = ref(0)
const heroMockup = ref(null)

function onHeroMove(e) {
  if (!heroMockup.value) return
  const r = heroMockup.value.getBoundingClientRect()
  mx.value = ((e.clientX - r.left) / r.width - 0.5) * 2
  my.value = ((e.clientY - r.top) / r.height - 0.5) * 2
}
function onHeroLeave() { mx.value = 0; my.value = 0 }

const mockupTransform = computed(() => ({
  transform: `perspective(1200px) rotateY(${mx.value * 5}deg) rotateX(${-my.value * 5}deg)`,
  transition: mx.value === 0 && my.value === 0 ? 'transform 0.6s cubic-bezier(.22,1,.36,1)' : 'transform 0.08s ease'
}))
const badge1Transform = computed(() => ({
  transform: `translate(${-mx.value * 12}px, ${-my.value * 12}px)`,
  transition: mx.value === 0 ? 'transform 0.6s cubic-bezier(.22,1,.36,1)' : 'transform 0.08s ease'
}))
const badge2Transform = computed(() => ({
  transform: `translate(${-mx.value * 18}px, ${-my.value * 18}px)`,
  transition: mx.value === 0 ? 'transform 0.6s cubic-bezier(.22,1,.36,1)' : 'transform 0.08s ease'
}))
const badge3Transform = computed(() => ({
  transform: `translate(${-mx.value * 15}px, ${-my.value * 15}px) translateY(-50%)`,
  transition: mx.value === 0 ? 'transform 0.6s cubic-bezier(.22,1,.36,1)' : 'transform 0.08s ease'
}))

const chatVisible = ref(0)
const cConversations = ref(0)
const cSatisfaction = ref(0)
const cSaved = ref(0)
const codeVisible = ref(0)
const checksVisible = ref(0)
const activeIndustry = ref(0)
const phoneRevealed = ref(false)

const cResolution = ref(0)
const cCostSaving = ref(0)
const cSatScore = ref(0)
const cLeads = ref(0)

const featurePhone = ref(null)
const pmx = ref(0)
const pmy = ref(0)

function onPhoneMove(e) {
  if (!featurePhone.value) return
  const r = featurePhone.value.getBoundingClientRect()
  pmx.value = ((e.clientX - r.left) / r.width - 0.5) * 2
  pmy.value = ((e.clientY - r.top) / r.height - 0.5) * 2
}
function onPhoneLeave() { pmx.value = 0; pmy.value = 0 }

const phoneTransform = computed(() => ({
  transform: `perspective(1200px) rotateY(${pmx.value * 8}deg) rotateX(${-pmy.value * 5}deg)`,
  transition: pmx.value === 0 && pmy.value === 0 ? 'transform 0.6s cubic-bezier(.22,1,.36,1)' : 'transform 0.08s ease'
}))
const phoneBadge1 = computed(() => ({
  transform: `translate(${-pmx.value * 10}px, ${-pmy.value * 10}px)`,
  transition: pmx.value === 0 ? 'transform 0.6s cubic-bezier(.22,1,.36,1)' : 'transform 0.08s ease'
}))
const phoneBadge2 = computed(() => ({
  transform: `translate(${-pmx.value * 16}px, ${-pmy.value * 16}px)`,
  transition: pmx.value === 0 ? 'transform 0.6s cubic-bezier(.22,1,.36,1)' : 'transform 0.08s ease'
}))
const phoneBadge3 = computed(() => ({
  transform: `translate(${-pmx.value * 14}px, ${-pmy.value * 14}px)`,
  transition: pmx.value === 0 ? 'transform 0.6s cubic-bezier(.22,1,.36,1)' : 'transform 0.08s ease'
}))

function countUp(target, end, duration, decimals = 0) {
  const s = performance.now()
  function tick(now) {
    const p = Math.min((now - s) / duration, 1)
    const e = 1 - Math.pow(1 - p, 3)
    target.value = parseFloat((e * end).toFixed(decimals))
    if (p < 1) requestAnimationFrame(tick)
  }
  requestAnimationFrame(tick)
}

watch(() => visible.hero, v => {
  if (v) {
    chatVisible.value = 0
    ;[600, 1400, 2200, 3000].forEach((d, i) => setTimeout(() => { chatVisible.value = i + 1 }, d))
  }
})

watch(() => visible.valueprop, v => {
  if (v) {
    countUp(cConversations, 8547, 2200)
    countUp(cSatisfaction, 96.4, 1800, 1)
    countUp(cSaved, 32.5, 2400, 1)
  }
})

watch(() => visible.process, v => {
  if (v) {
    codeVisible.value = 0
    checksVisible.value = 0
    ;[600, 1200, 1800, 2400].forEach((d, i) => setTimeout(() => { codeVisible.value = i + 1 }, d))
    ;[300, 700, 1100, 1500].forEach((d, i) => setTimeout(() => { checksVisible.value = i + 1 }, d))
  }
})

watch(() => visible.features, v => {
  if (v) phoneRevealed.value = true
})

watch(() => visible.benefits, v => {
  if (v) {
    countUp(cResolution, 85, 1800)
    countUp(cCostSaving, 60, 2000)
    countUp(cSatScore, 96.4, 1600, 1)
    countUp(cLeads, 45, 2200)
  }
})

onMounted(() => {
  observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) visible[entry.target.dataset.animate] = true
    })
  }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' })
  document.querySelectorAll('[data-animate]').forEach(el => observer.observe(el))
})

onUnmounted(() => { if (observer) observer.disconnect() })

const impressiveStats = computed(() => [
  { value: '85%', label: tr('Risoluzione', 'Resolution'), description: tr('Automatica senza agente', 'Automatic without agent') },
  { value: '<1s', label: tr('Risposta', 'Response'), description: tr('Tempo medio di risposta', 'Average response time') },
  { value: '24/7', label: tr('Disponibilità', 'Availability'), description: tr('Sempre online, nessuna pausa', 'Always online, no downtime') },
  { value: '4+', label: tr('Lingue', 'Languages'), description: tr('IT, DE, FR, EN e altre', 'IT, DE, FR, EN and more') }
])

const features = [
  { icon: Bot, title: t('chatbot.features.ai.title'), description: t('chatbot.features.ai.description'), bg: 'bg-blue-500/10', iconColor: 'text-blue-500' },
  { icon: Clock, title: t('chatbot.features.availability.title'), description: t('chatbot.features.availability.description'), bg: 'bg-green-500/10', iconColor: 'text-green-500' },
  { icon: Globe, title: t('chatbot.features.multilingual.title'), description: t('chatbot.features.multilingual.description'), bg: 'bg-purple-500/10', iconColor: 'text-purple-500' },
  { icon: Zap, title: t('chatbot.features.integration.title'), description: t('chatbot.features.integration.description'), bg: 'bg-yellow-500/10', iconColor: 'text-yellow-500' },
  { icon: Users, title: t('chatbot.features.scalability.title'), description: t('chatbot.features.scalability.description'), bg: 'bg-orange-500/10', iconColor: 'text-orange-500' },
  { icon: Shield, title: t('chatbot.features.security.title'), description: t('chatbot.features.security.description'), bg: 'bg-teal-500/10', iconColor: 'text-teal-500' }
]

const useCases = [
  { icon: HeadphonesIcon, title: t('chatbot.useCases.customerService.title'), description: t('chatbot.useCases.customerService.description'), bg: 'bg-blue-500/10', iconColor: 'text-blue-500' },
  { icon: ShoppingCart, title: t('chatbot.useCases.sales.title'), description: t('chatbot.useCases.sales.description'), bg: 'bg-green-500/10', iconColor: 'text-green-500' },
  { icon: BookOpen, title: t('chatbot.useCases.support.title'), description: t('chatbot.useCases.support.description'), bg: 'bg-purple-500/10', iconColor: 'text-purple-500' },
  { icon: Target, title: t('chatbot.useCases.training.title'), description: t('chatbot.useCases.training.description'), bg: 'bg-orange-500/10', iconColor: 'text-orange-500' }
]

const benefits = [
  { icon: Gauge, title: t('chatbot.benefits.time.title'), description: t('chatbot.benefits.time.description'), bg: 'bg-blue-500/10', iconColor: 'text-blue-500' },
  { icon: DollarSign, title: t('chatbot.benefits.cost.title'), description: t('chatbot.benefits.cost.description'), bg: 'bg-green-500/10', iconColor: 'text-green-500' },
  { icon: TrendingUp, title: t('chatbot.benefits.satisfaction.title'), description: t('chatbot.benefits.satisfaction.description'), bg: 'bg-purple-500/10', iconColor: 'text-purple-500' },
  { icon: RefreshCcw, title: t('chatbot.benefits.analytics.title'), description: t('chatbot.benefits.analytics.description'), bg: 'bg-orange-500/10', iconColor: 'text-orange-500' }
]

const industryChats = [
  {
    domain: 'shop.moda-ch.com/chat',
    botName: 'Fashion Assistant',
    messages: [
      { from: 'bot', text: 'Benvenuto! Cerchi qualcosa di specifico oggi?' },
      { from: 'user', text: 'Vorrei una giacca invernale' },
      { from: 'bot', text: 'Ho trovato 3 giacche invernali perfette per te! La nostra più venduta è la Alpine Pro a CHF 189. Vuoi vederla?' },
      { from: 'user', text: 'Si, mostrami!' }
    ]
  },
  {
    domain: 'clinica-salus.ch/assistente',
    botName: 'Assistente Medico',
    messages: [
      { from: 'bot', text: 'Buongiorno! Come posso assisterla?' },
      { from: 'user', text: 'Devo prenotare una visita' },
      { from: 'bot', text: 'Perfetto! Il Dr. Rossi ha disponibilità giovedì 14:00 o venerdì 10:00. Quale preferisce?' },
      { from: 'user', text: 'Giovedì alle 14 va bene' },
      { from: 'bot', text: 'Prenotato! Riceverà una conferma via email. Ha bisogno di altro?' }
    ]
  },
  {
    domain: 'academy.learn-ch.com',
    botName: 'Tutor AI',
    messages: [
      { from: 'bot', text: 'Ciao studente! Come posso aiutarti oggi?' },
      { from: 'user', text: 'Non capisco le equazioni differenziali' },
      { from: 'bot', text: 'Nessun problema! Un\'equazione differenziale descrive come una quantità cambia nel tempo. Pensa alla velocità: è la derivata della posizione. Vuoi fare un esercizio insieme?' }
    ]
  },
  {
    domain: 'travel-swiss.ch/concierge',
    botName: 'Travel Concierge',
    messages: [
      { from: 'bot', text: 'Benvenuto! Dove vorresti viaggiare?' },
      { from: 'user', text: 'Weekend a Lugano per 2' },
      { from: 'bot', text: 'Ottima scelta! Ho trovato il pacchetto "Lugano Romantica": 2 notti al Hotel Splendide + cena con vista lago a CHF 450. Include anche un tour in barca!' }
    ]
  },
  {
    domain: 'finanza-sicura.ch/advisor',
    botName: 'Financial Advisor',
    messages: [
      { from: 'bot', text: 'Buongiorno! Come posso aiutarla con le sue finanze?' },
      { from: 'user', text: 'Vorrei informazioni sui mutui' },
      { from: 'bot', text: 'Certamente! Attualmente offriamo tassi dal 1.8% per mutui a tasso fisso 10 anni. Con un reddito di CHF 120k, può accedere fino a CHF 800k. Vuole una simulazione personalizzata?' }
    ]
  },
  {
    domain: 'techfab.swiss/support',
    botName: 'Support Tecnico',
    messages: [
      { from: 'bot', text: 'Benvenuto al supporto tecnico! Come posso aiutarla?' },
      { from: 'user', text: 'La macchina CNC dà errore E-304' },
      { from: 'bot', text: 'Errore E-304 indica un problema di calibrazione asse Z. Segua questi step: 1) Spegnere la macchina, 2) Verificare il sensore Z, 3) Eseguire auto-calibrazione. Serve assistenza remota?' }
    ]
  }
]

const integrationPlatforms = [
  { icon: MessageCircle, name: 'WhatsApp' },
  { icon: Instagram, name: 'Instagram' },
  { icon: Facebook, name: 'Facebook' },
  { icon: Smartphone, name: 'Telegram' },
  { icon: Globe, name: 'Website' },
  { icon: Webhook, name: 'API/Webhook' }
]

const aiTech = computed(() => [
  { icon: Brain, title: tr('NLP Avanzato', 'Advanced NLP'), desc: tr('Comprensione naturale del linguaggio', 'Natural language understanding'), bg: 'bg-blue-500/10', iconColor: 'text-blue-500' },
  { icon: Cpu, title: 'Machine Learning', desc: tr('Apprendimento continuo dai dati', 'Continuous learning from data'), bg: 'bg-purple-500/10', iconColor: 'text-purple-500' },
  { icon: Database, title: 'Knowledge Base', desc: tr('Risposte basate sui tuoi dati', 'Answers based on your data'), bg: 'bg-green-500/10', iconColor: 'text-green-500' }
])

const ctaStats = computed(() => [
  { value: '50+', label: tr('Chatbot attivi', 'Active chatbots') },
  { value: '2M+', label: tr('Conversazioni gestite', 'Handled conversations') },
  { value: '99.9%', label: tr('Uptime garantito', 'Guaranteed uptime') },
  { value: '<1s', label: tr('Tempo di risposta', 'Response time') }
])

const developmentSteps = [
  { title: t('chatbot.development.step1.title'), description: t('chatbot.development.step1.description'), details: [t('chatbot.development.step1.detail1'), t('chatbot.development.step1.detail2'), t('chatbot.development.step1.detail3')] },
  { title: t('chatbot.development.step2.title'), description: t('chatbot.development.step2.description'), details: [t('chatbot.development.step2.detail1'), t('chatbot.development.step2.detail2'), t('chatbot.development.step2.detail3')] },
  { title: t('chatbot.development.step3.title'), description: t('chatbot.development.step3.description'), details: [t('chatbot.development.step3.detail1'), t('chatbot.development.step3.detail2'), t('chatbot.development.step3.detail3')] },
  { title: t('chatbot.development.step4.title'), description: t('chatbot.development.step4.description'), details: [t('chatbot.development.step4.detail1'), t('chatbot.development.step4.detail2'), t('chatbot.development.step4.detail3')] }
]

const industries = [
  { icon: Building, title: t('chatbot.industries.ecommerce.title'), description: t('chatbot.industries.ecommerce.description'), applications: [t('chatbot.industries.ecommerce.app1'), t('chatbot.industries.ecommerce.app2'), t('chatbot.industries.ecommerce.app3')] },
  { icon: Heart, title: t('chatbot.industries.healthcare.title'), description: t('chatbot.industries.healthcare.description'), applications: [t('chatbot.industries.healthcare.app1'), t('chatbot.industries.healthcare.app2'), t('chatbot.industries.healthcare.app3')] },
  { icon: GraduationCap, title: t('chatbot.industries.education.title'), description: t('chatbot.industries.education.description'), applications: [t('chatbot.industries.education.app1'), t('chatbot.industries.education.app2'), t('chatbot.industries.education.app3')] },
  { icon: Car, title: t('chatbot.industries.travel.title'), description: t('chatbot.industries.travel.description'), applications: [t('chatbot.industries.travel.app1'), t('chatbot.industries.travel.app2'), t('chatbot.industries.travel.app3')] },
  { icon: CreditCard, title: t('chatbot.industries.finance.title'), description: t('chatbot.industries.finance.description'), applications: [t('chatbot.industries.finance.app1'), t('chatbot.industries.finance.app2'), t('chatbot.industries.finance.app3')] },
  { icon: Settings, title: t('chatbot.industries.manufacturing.title'), description: t('chatbot.industries.manufacturing.description'), applications: [t('chatbot.industries.manufacturing.app1'), t('chatbot.industries.manufacturing.app2'), t('chatbot.industries.manufacturing.app3')] }
]

const techStack = [
  { icon: Bot, name: t('chatbot.techStack.n8n.name'), description: t('chatbot.techStack.n8n.description'), bg: 'bg-blue-500/10', iconColor: 'text-blue-400', features: [t('chatbot.techStack.n8n.features.visual'), t('chatbot.techStack.n8n.features.integrations'), t('chatbot.techStack.n8n.features.webhooks'), t('chatbot.techStack.n8n.features.errorHandling')] },
  { icon: Brain, name: t('chatbot.techStack.botpress.name'), description: t('chatbot.techStack.botpress.description'), bg: 'bg-green-500/10', iconColor: 'text-green-400', features: [t('chatbot.techStack.botpress.features.nlp'), t('chatbot.techStack.botpress.features.multichannel'), t('chatbot.techStack.botpress.features.analytics'), t('chatbot.techStack.botpress.features.modules')] },
  { icon: Code, name: t('chatbot.techStack.python.name'), description: t('chatbot.techStack.python.description'), bg: 'bg-purple-500/10', iconColor: 'text-purple-400', features: [t('chatbot.techStack.python.features.langchain'), t('chatbot.techStack.python.features.models'), t('chatbot.techStack.python.features.architecture'), t('chatbot.techStack.python.features.flexibility')] }
]

const faqs = [
  { question: t('chatbot.faq.q1.question'), answer: t('chatbot.faq.q1.answer') },
  { question: t('chatbot.faq.q2.question'), answer: t('chatbot.faq.q2.answer') },
  { question: t('chatbot.faq.q3.question'), answer: t('chatbot.faq.q3.answer') },
  { question: t('chatbot.faq.q4.question'), answer: t('chatbot.faq.q4.answer') },
  { question: t('chatbot.faq.q5.question'), answer: t('chatbot.faq.q5.answer') },
  { question: t('chatbot.faq.q6.question'), answer: t('chatbot.faq.q6.answer') }
]

defineOptions({ name: 'ChatbotPage' })
</script>

<style scoped>
.hero-gradient-text {
  background: linear-gradient(135deg, #60a5fa 0%, #3b82f6 50%, #2563eb 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
.tabular-nums { font-variant-numeric: tabular-nums; }

.anim-el {
  opacity: 0;
  transform: translateY(24px);
  transition: opacity 0.7s cubic-bezier(.22,1,.36,1), transform 0.7s cubic-bezier(.22,1,.36,1);
}
.is-visible .anim-el { opacity: 1; transform: translateY(0); }
.d1 { transition-delay: 0.05s; }
.d2 { transition-delay: 0.15s; }
.d3 { transition-delay: 0.25s; }
.d4 { transition-delay: 0.35s; }
.d5 { transition-delay: 0.45s; }
.d6 { transition-delay: 0.55s; }
.d7 { transition-delay: 0.65s; }
.d8 { transition-delay: 0.75s; }
.d9 { transition-delay: 0.85s; }

.chat-mockup { will-change: transform; transform-style: preserve-3d; }
.floating-badge { will-change: transform; }

.particle {
  position: absolute;
  bottom: -10px;
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: rgba(96, 165, 250, 0.4);
  animation: particleRise linear infinite;
}
@keyframes particleRise {
  0% { transform: translateY(0) translateX(0); opacity: 0; }
  5% { opacity: 0.8; }
  50% { transform: translateY(-50vh) translateX(20px); opacity: 0.4; }
  100% { transform: translateY(-100vh) translateX(-10px); opacity: 0; }
}

.line-draw {
  stroke-dasharray: 320;
  stroke-dashoffset: 320;
  transition: stroke-dashoffset 2.5s cubic-bezier(.22,1,.36,1) 0.3s;
}
.is-visible .line-draw { stroke-dashoffset: 0; }
.line-dot { opacity: 0; transition: opacity 0.3s ease 2.5s; }
.is-visible .line-dot { opacity: 1; }

.bar-grow {
  height: 0;
  transition: height 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.is-visible .bar-grow { height: var(--h); }
.bar-grow:nth-child(1)  { transition-delay: 0.1s; }
.bar-grow:nth-child(2)  { transition-delay: 0.16s; }
.bar-grow:nth-child(3)  { transition-delay: 0.22s; }
.bar-grow:nth-child(4)  { transition-delay: 0.28s; }
.bar-grow:nth-child(5)  { transition-delay: 0.34s; }
.bar-grow:nth-child(6)  { transition-delay: 0.4s; }
.bar-grow:nth-child(7)  { transition-delay: 0.46s; }
.bar-grow:nth-child(8)  { transition-delay: 0.52s; }
.bar-grow:nth-child(9)  { transition-delay: 0.58s; }

.glitch-shake { animation: glitchShake 4s ease-in-out infinite; }
@keyframes glitchShake {
  0%, 92%, 100% { transform: translate(0, 0); }
  93% { transform: translate(-2px, 1px); }
  94% { transform: translate(2px, -1px); }
  95% { transform: translate(-1px, -1px); }
  96% { transform: translate(1px, 1px); }
  97% { transform: translate(0, 0); }
}

.after-glow { animation: glowPulse 3s ease-in-out infinite; }
@keyframes glowPulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0); }
  50% { box-shadow: 0 0 20px 2px rgba(34, 197, 94, 0.08); }
}

.loading-stuck { animation: loadingStuck 2s ease-in-out infinite; }
@keyframes loadingStuck {
  0%, 100% { width: 30%; }
  50% { width: 38%; }
}
.loading-done {
  width: 0;
  transition: width 1.2s cubic-bezier(.22,1,.36,1) 0.4s;
}
.is-visible .loading-done { width: 100%; }

.typing-cursor { animation: cursorBlink 1s step-end infinite; color: #60a5fa; font-weight: normal; }
@keyframes cursorBlink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }
.code-line { animation: codeFadeIn 0.3s ease both; }
@keyframes codeFadeIn { from { opacity: 0; transform: translateX(-6px); } to { opacity: 1; transform: translateX(0); } }

.typing-dot { animation: typingBounce 1.4s ease-in-out infinite; }
@keyframes typingBounce {
  0%, 60%, 100% { transform: translateY(0); }
  30% { transform: translateY(-4px); }
}

.phone-mockup { will-change: transform; transform-style: preserve-3d; }

.phone-msg {
  opacity: 0;
  transform: translateY(8px);
  transition: opacity 0.5s cubic-bezier(.22,1,.36,1), transform 0.5s cubic-bezier(.22,1,.36,1);
}
.phone-chat-revealed .phone-msg { opacity: 1; transform: translateY(0); }
.phone-chat-revealed .phone-msg-1 { transition-delay: 0.1s; }
.phone-chat-revealed .phone-msg-2 { transition-delay: 0.3s; }
.phone-chat-revealed .phone-msg-3 { transition-delay: 0.6s; }
.phone-chat-revealed .phone-msg-4 { transition-delay: 1.0s; }
.phone-chat-revealed .phone-msg-5 { transition-delay: 1.3s; }

.preview-fade-enter-active,
.preview-fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}
.preview-fade-enter-from {
  opacity: 0;
  transform: translateY(10px);
}
.preview-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
